import React, { useCallback, useMemo } from 'react';
import { Box, Slider } from '@mui/material';
import clsx from 'clsx';
import Typography from '@ui/Typography';
import { StudioCueType } from './Cue';
import useSliderRange, { getSliderTrackStyles } from './utils/utils';
import useStyles from './styles';

interface StudioCueSliderMarks {
  startLabel: string;
  endLabel: string;
}

interface StudioCueSliderProps {
  sliderValue: number;
  onChange: (newValue: number) => void;
  labels: StudioCueSliderMarks;
  cueType: StudioCueType;
  isActive?: boolean;
}

interface StudioCueSliderLabelProps {
  label: string;
  currentSliderValue: number;
  labelValue: number;
  isActive?: boolean;
  classNames: {
    label: string;
    highlight: string;
    inactive: string;
  };
}

/**
 * Render text label with ability to be highlighted if slider displays specific
 */
const StudioCueSliderLabel = (props: StudioCueSliderLabelProps) => {
  const {
    label: labelDisplay,
    currentSliderValue,
    labelValue,
    isActive,
    classNames: { label, highlight, inactive },
  } = props;
  return (
    <Typography
      className={clsx(label, {
        [highlight]: currentSliderValue === labelValue,
        [inactive]: !isActive,
      })}
      variant={'pSm'}
    >
      {labelDisplay}
    </Typography>
  );
};

/**
 * Slider wrapper for cues with styling and labels
 */
const StudioCueSlider = (props: StudioCueSliderProps) => {
  const { sliderValue, labels, cueType, isActive, onChange } = props;
  const { min, max, step, marks, noCueValue } = useSliderRange({});
  const trackStyles = getSliderTrackStyles(sliderValue);
  const { classes } = useStyles({ trackStyles });

  const thumbClass = useMemo(
    () => (isActive ? classes[`thumb-${cueType}`] : classes[`thumb-inactive`]),
    [classes, cueType, isActive]
  );

  const getLabelClasses = useCallback(
    (position: 'start' | 'middle' | 'end') => {
      const labelClasses = {
        highlight: isActive
          ? classes[`highlight-${cueType}` as keyof typeof classes]
          : '',
        label: classes.sliderLabels,
        inactive: classes[`highlight-inactive`],
      };

      switch (position) {
        case 'middle':
          labelClasses.label = clsx(labelClasses.label, classes.middleLabel);
          break;
        case 'end':
          labelClasses.label = clsx(labelClasses.label, classes.endLabel);
          break;
        default:
          break;
      }
      return labelClasses;
    },
    [classes, cueType, isActive]
  );

  const handleChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      onChange(newValue);
    }
  };

  return (
    <>
      <Slider
        className={clsx(classes.slider, thumbClass)}
        track="normal"
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        marks={marks}
        valueLabelDisplay={'auto'}
        onChange={handleChange}
        disabled={!isActive}
      />
      <Box position={'relative'}>
        <StudioCueSliderLabel
          label={labels.startLabel}
          currentSliderValue={sliderValue}
          labelValue={min}
          isActive={isActive}
          classNames={getLabelClasses('start')}
        />
        <StudioCueSliderLabel
          label={'No Cue'}
          currentSliderValue={sliderValue}
          labelValue={noCueValue}
          isActive={isActive}
          classNames={getLabelClasses('middle')}
        />
        <StudioCueSliderLabel
          label={labels.endLabel}
          currentSliderValue={sliderValue}
          labelValue={max}
          isActive={isActive}
          classNames={getLabelClasses('end')}
        />
      </Box>
    </>
  );
};

export default StudioCueSlider;
